/**
 * UploadDocsPane - Document upload with facet suggestions
 * Drag-and-drop interface with context-aware facet recommendations
 */

import { useState, useRef, useCallback } from 'react'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useProjectStore } from '@/stores/projectStore'
import { useAuthStore } from '@/stores/authStore'
import { Button } from '@/components/ui/button'
import { FileUp, File, X, Loader2, CheckCircle, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { API_URL, getSessionToken } from '@/lib/api/client'

interface UploadedFile {
  id: string
  name: string
  status: 'uploading' | 'success' | 'error'
  progress: number
  error?: string
}

export function UploadDocsPane() {
  const { workspaceContext } = useWorkspaceStore()
  const { currentProject } = useProjectStore()
  const { currentAccount } = useAuthStore()

  const [files, setFiles] = useState<UploadedFile[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Get suggested facets based on workspace mode
  const suggestedFacets = getSuggestedFacets(workspaceContext.mode)

  // Handle drag events
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const droppedFiles = Array.from(e.dataTransfer.files)
    handleFiles(droppedFiles)
  }, [])

  // Handle file selection
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files ? Array.from(e.target.files) : []
    handleFiles(selectedFiles)
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  // Upload files
  const handleFiles = async (filesToUpload: File[]) => {
    if (!currentAccount || !currentProject) return

    for (const file of filesToUpload) {
      const fileId = `${Date.now()}-${file.name}`
      const uploadFile: UploadedFile = {
        id: fileId,
        name: file.name,
        status: 'uploading',
        progress: 0,
      }

      setFiles((prev) => [...prev, uploadFile])

      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('contentType', file.type)

        const token = getSessionToken()
        const response = await fetch(
          `${API_URL}/api/accounts/${currentAccount.id}/projects/${currentProject.id}/sources`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          }
        )

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.statusText}`)
        }

        // Success
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileId ? { ...f, status: 'success', progress: 100 } : f
          )
        )
      } catch (error) {
        // Error
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileId
              ? {
                  ...f,
                  status: 'error',
                  progress: 0,
                  error: error instanceof Error ? error.message : 'Upload failed',
                }
              : f
          )
        )
      }
    }
  }

  // Remove file from list
  const removeFile = (fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId))
  }

  return (
    <div className="space-y-4">
      {/* Upload Zone */}
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={cn(
          'border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all',
          isDragging
            ? 'border-blue-400 bg-blue-50'
            : 'border-warm-300 hover:border-warm-400 hover:bg-warm-50'
        )}
      >
        <FileUp
          size={32}
          className={cn('mx-auto mb-2', isDragging ? 'text-blue-500' : 'text-warm-400')}
        />
        <p className="text-sm font-medium mb-1">
          {isDragging ? 'Drop files here' : 'Drop files here'}
        </p>
        <p className="text-xs text-text-muted">or click to browse</p>
        <Button size="sm" variant="outline" className="mt-3 pointer-events-none">
          Browse Files
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          accept=".pdf,.doc,.docx,.txt,.md"
        />
      </div>

      {/* Suggested Facets */}
      {suggestedFacets.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-warm-700">
            Suggested entity types for {currentPhase?.name || 'this phase'}:
          </div>
          <div className="flex flex-wrap gap-1">
            {suggestedFacets.map((facet) => (
              <span
                key={facet}
                className="px-2 py-1 text-xs rounded bg-warm-100 text-warm-700"
              >
                {facet}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Uploaded Files */}
      {files.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-warm-700">Recent uploads</div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {files.map((file) => (
              <div
                key={file.id}
                className="flex items-center gap-2 p-2 rounded bg-warm-50 text-xs"
              >
                {/* Status Icon */}
                {file.status === 'uploading' && (
                  <Loader2 className="w-4 h-4 animate-spin text-blue-500 flex-shrink-0" />
                )}
                {file.status === 'success' && (
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                )}
                {file.status === 'error' && (
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                )}

                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{file.name}</div>
                  {file.status === 'error' && file.error && (
                    <div className="text-red-600 text-xs">{file.error}</div>
                  )}
                  {file.status === 'uploading' && (
                    <div className="w-full bg-warm-200 rounded-full h-1 mt-1">
                      <div
                        className="bg-blue-500 h-1 rounded-full transition-all"
                        style={{ width: `${file.progress}%` }}
                      />
                    </div>
                  )}
                </div>

                {/* Remove Button */}
                {file.status !== 'uploading' && (
                  <button
                    onClick={() => removeFile(file.id)}
                    className="p-1 hover:bg-warm-200 rounded transition-colors flex-shrink-0"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {files.length === 0 && (
        <div className="text-xs text-text-muted text-center py-4">
          No files uploaded yet
        </div>
      )}

      {/* Info */}
      <div className="text-xs p-2 rounded bg-warm-100 text-warm-700">
        Documents will be analyzed for entity extraction and added to your knowledge
        base
      </div>
    </div>
  )
}

function getSuggestedFacets(phaseName?: string): string[] {
  if (!phaseName) return []

  const facetMap: Record<string, string[]> = {
    'Customer Discovery': ['customer_segment', 'persona_attribute', 'demographic'],
    'Problem Validation': ['customer_job', 'customer_pain', 'customer_gain'],
    'Value Design': ['value_proposition', 'channel', 'feature_request'],
    'Solution Design': ['product_feature', 'requirement', 'user_story'],
    'Business Model': ['revenue_stream', 'cost', 'pricing_model'],
  }

  return facetMap[phaseName] || []
}
